import { CaretDownGreyIcon, CaretRightIcon, ExpandImageIcon } from 'assets/icons';
import { FC, useState } from 'react';
import { TimelineProps } from 'types/timelineTypes';

const Timeline: FC<TimelineProps> = ({
  status,
  timeline,
  onCTAClick,
  className = '',
  showExpandToggle = true,
  defaultExpanded = false,
  hasImageViewer = false,
  onImagePreview,
  attachmentClassName = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleTimeline = () => {
    setIsExpanded(prev => !prev);
  };

  const handleCTAClick = (action: string) => {
    if (onCTAClick) {
      onCTAClick(action);
    }
  };

  const { className: statusClassName, Icon, status: statusText } = status;

  return (
    <div className={`rounded-md border border-border ${className}`}>
      <div className={`${statusClassName} flex items-center rounded-tr-md py-3.5 px-4`}>
        <Icon className="mr-2.5 shrink-0" width={18} height={18} />
        <div className="text-17px font-semibold text-primaryText">{statusText}</div>
        {showExpandToggle && (
          <div
            className="ml-auto flex cursor-pointer items-center gap-2 
            justify-self-end text-sbase font-semibold text-primaryText"
            onClick={toggleTimeline}
            tabIndex={0}
            role="button"
            aria-label={isExpanded ? 'Collapse timeline' : 'Expand timeline'}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleTimeline();
              }
            }}>
            {isExpanded ? 'Show less' : 'Show more'}
            <CaretDownGreyIcon className={isExpanded ? 'rotate-180' : ''} />
          </div>
        )}
      </div>
      <div className="p-5 pb-0">
        {timeline.map(
          (event, index) =>
            (isExpanded || (!isExpanded && event.webInfo.summary)) && (
              <div className="flex gap-3.5" key={event.title + index}>
                <div className="flex flex-col items-center">
                  <div
                    className="h-[18px] w-[18px] shrink-0 rounded-full"
                    style={{ backgroundColor: event.colourCode }}></div>
                  {index !== timeline.length - 1 && <div className="h-full w-px bg-border"></div>}
                </div>
                <div className="flex flex-col gap-1 pb-5">
                  {event.title && <div className="text-17px font-semibold text-primaryText">{event.title}</div>}
                  {event.subTitle && <div className="text-sbase font-medium text-primaryText">{event.subTitle}</div>}
                  {event.reason && <div className="text-sbase text-primaryText">Reason: {event.reason}</div>}
                  {event.timestamp && (
                    <div className="text-px13 font-normal text-accentText">
                      {event.timestamp} {event.doneBy ? `by ${event.doneBy}` : ''}
                    </div>
                  )}
                  {event.webInfo.cta.text && (
                    <div
                      className={`flex w-fit items-center justify-between gap-2
                  rounded-md bg-secondaryBtn py-1.5 px-3.5 text-sbase text-secondary 
                  ${event.webInfo.cta.action ? 'cursor-pointer' : ''}`}
                      onClick={() => event.webInfo.cta.action && handleCTAClick(event.webInfo.cta.action)}
                      tabIndex={event.webInfo.cta.action ? 0 : -1}
                      role={event.webInfo.cta.action ? 'button' : undefined}
                      aria-label={event.webInfo.cta.action ? `Action: ${event.webInfo.cta.text}` : undefined}
                      onKeyDown={e => {
                        if (event.webInfo.cta.action && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleCTAClick(event.webInfo.cta.action);
                        }
                      }}>
                      {event.webInfo.cta.text}
                      {event.webInfo.cta.action && (
                        <CaretRightIcon className="path-fill-current path text-border" width={18} />
                      )}
                    </div>
                  )}
                  {event.attachments?.length > 0 && (
                    <div className={`mt-4 flex flex-wrap gap-4 ${attachmentClassName}`}>
                      {event.attachments.map((attachment, attachmentIndex) => (
                        <div
                          key={`timeline-event-${index}-attachment-${attachmentIndex}`}
                          className="relative h-[60px] max-h-[60px] w-[60px]">
                          <img
                            src={attachment.urlLink}
                            alt={attachment.title || `Attachment ${attachmentIndex + 1}`}
                            height={60}
                            width={60}
                            className="h-[60px] max-h-[60px] w-[60px] rounded object-cover"
                          />
                          {hasImageViewer && onImagePreview && (
                            <ExpandImageIcon
                              className="absolute top-1 right-[3px] cursor-pointer"
                              onClick={() => onImagePreview(attachmentIndex, event)}
                              tabIndex={0}
                              role="button"
                              aria-label={`Preview attachment: ${
                                attachment.title || `Attachment ${attachmentIndex + 1}`
                              }`}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onImagePreview(attachmentIndex, event);
                                }
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
};

export default Timeline;
